import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function Home() {
  const [coins, setCoins] = useState([]);
  const [filteredCoins, setFilteredCoins] = useState([]);
  const [displayedCoins, setDisplayedCoins] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCoins();
    const interval = setInterval(fetchCoins, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCoins(coins);
      setDisplayedCoins(10); // Reset to 10 when search is cleared
    } else {
      const filtered = coins.filter(coin =>
        coin.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coin.symbol.toLowerCase().replace('USDT', '').includes(searchTerm.toLowerCase())
      );
      setFilteredCoins(filtered);
      setDisplayedCoins(filtered.length); // Show all when searching
    }
  }, [searchTerm, coins]);

  const fetchCoins = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.binance.com/api/v3/ticker/24hr');
      if (!response.ok) {
        throw new Error('Failed to fetch coins');
      }
      const data = await response.json();
      const usdtPairs = data
        .filter(coin => coin.symbol.endsWith('USDT'))
        .sort((a, b) => parseFloat(b.quoteVolume) - parseFloat(a.quoteVolume))
        .slice(0, 100);
      setCoins(usdtPairs);
      setFilteredCoins(usdtPairs);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching coins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCoinClick = (symbol) => {
    navigate(`/coin/${symbol}`);
  };

  const handleLoadMore = () => {
    setDisplayedCoins(prev => Math.min(prev + 10, filteredCoins.length));
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (num < 0.01) {
      return num.toFixed(8);
    } else if (num < 1) {
      return num.toFixed(4);
    } else {
      return num.toFixed(2);
    }
  };

  const formatVolume = (volume) => {
    const num = parseFloat(volume);
    if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(2)}B`;
    } else if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  const coinsToDisplay = searchTerm.trim() === '' 
    ? filteredCoins.slice(0, displayedCoins)
    : filteredCoins;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 bg-clip-text text-transparent">
            CryptoVista - Cryptocurrency Market
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Track real-time cryptocurrency prices, market trends, and trading volumes from the world's largest crypto exchange. 
            Get instant access to comprehensive market data and make informed investment decisions.
          </p>
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search coins (e.g., BTC, ETH, SOL)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pl-12 pr-12 text-lg border-2 border-purple-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-lg transition-all duration-200"
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
            <p className="text-gray-600 text-lg">Loading coins...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="max-w-md mx-auto bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-800 mb-4 font-semibold">Error: {error}</p>
            <button
              onClick={fetchCoins}
              className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-colors font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Coins Section */}
        {!loading && !error && (
          <div className="mb-16">
            <div className="mb-6 flex justify-between items-center">
              <div className="text-gray-600 font-medium">
                Showing <span className="text-purple-600 font-bold">{coinsToDisplay.length}</span> of <span className="text-purple-600 font-bold">{filteredCoins.length}</span> coins
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {coinsToDisplay.length > 0 ? (
                coinsToDisplay.map((coin) => (
                  <div
                    key={coin.symbol}
                    onClick={() => handleCoinClick(coin.symbol)}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 border border-gray-100 hover:border-purple-300"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{coin.symbol.replace('USDT', '')}</h3>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">{coin.symbol}</span>
                    </div>
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-gray-900">${formatPrice(coin.lastPrice)}</span>
                        <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                          parseFloat(coin.priceChangePercent) >= 0
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {parseFloat(coin.priceChangePercent) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(coin.priceChangePercent)).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">24h High</span>
                        <span className="font-semibold text-gray-900">${formatPrice(coin.highPrice)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">24h Low</span>
                        <span className="font-semibold text-gray-900">${formatPrice(coin.lowPrice)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">24h Volume</span>
                        <span className="font-semibold text-purple-600">{formatVolume(coin.quoteVolume)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No coins found matching "{searchTerm}"</p>
                </div>
              )}
            </div>
            
            {/* Load More Button */}
            {searchTerm.trim() === '' && displayedCoins < filteredCoins.length && (
              <div className="text-center">
                <button
                  onClick={handleLoadMore}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:via-pink-700 hover:to-violet-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Load More ({filteredCoins.length - displayedCoins} remaining)
                </button>
              </div>
            )}
          </div>
        )}

        {/* About Our Website Section */}
        <div className="mt-20 space-y-16">
          {/* Features Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 border border-gray-100">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 bg-clip-text text-transparent">
              Why Choose CryptoVista?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Data</h3>
                <p className="text-gray-600">Get live cryptocurrency prices and market data updated every 30 seconds directly from Binance API.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Comprehensive Analytics</h3>
                <p className="text-gray-600">Access detailed coin information including 24h price history, trading volumes, and market statistics.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mobile Friendly</h3>
                <p className="text-gray-600">Fully responsive design that works seamlessly on desktop, tablet, and mobile devices.</p>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 rounded-2xl shadow-xl p-8 sm:p-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center">Platform Statistics</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">100+</div>
                <div className="text-purple-100">Cryptocurrencies</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">24/7</div>
                <div className="text-purple-100">Real-Time Updates</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">100%</div>
                <div className="text-purple-100">Free to Use</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">0</div>
                <div className="text-purple-100">Registration Required</div>
              </div>
            </div>
          </div>

          {/* How It Works Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 border border-gray-100">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 bg-clip-text text-transparent">
              How It Works
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">1</div>
                <h3 className="font-semibold text-gray-900 mb-2">Browse Coins</h3>
                <p className="text-gray-600 text-sm">Explore top cryptocurrencies by trading volume</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">2</div>
                <h3 className="font-semibold text-gray-900 mb-2">Search & Filter</h3>
                <p className="text-gray-600 text-sm">Find specific coins using our search functionality</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">3</div>
                <h3 className="font-semibold text-gray-900 mb-2">View Details</h3>
                <p className="text-gray-600 text-sm">Click any coin to see comprehensive market data</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">4</div>
                <h3 className="font-semibold text-gray-900 mb-2">Make Decisions</h3>
                <p className="text-gray-600 text-sm">Use real-time data to inform your investments</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 sm:p-12 text-white text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Explore the Markets?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Start tracking your favorite cryptocurrencies today. No registration required, completely free!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/about"
                className="px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                Learn More
              </Link>
              <Link
                to="/faq"
                className="px-8 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors shadow-lg"
              >
                View FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
